// services/SocketService.ts

import { io, ManagerOptions, Socket, SocketOptions } from 'socket.io-client';

class SocketService {
  private static instance: SocketService;
  private socket: Socket | null = null;
  private isConnected: boolean = false;

  private constructor() {
    // Private constructor to prevent direct instantiation
  }

  public static getInstance(): SocketService {
    if (!SocketService.instance) {
      SocketService.instance = new SocketService();
    }
    return SocketService.instance;
  }

  public connect(url: string, opt?: Partial<ManagerOptions & SocketOptions>): void {
    if (!this.socket) {
      this.socket = io(url, opt);
      this.isConnected = true;

      this.socket.on('connect', () => {
        this.isConnected = true;
        console.log('[Socket] connected', { url, id: this.socket?.id });
      });

      this.socket.on('connect_error', (err: Error) => {
        this.isConnected = false;
        console.error('[Socket] connect_error', { url, message: err.message, error: err });
      });

      // Reconnection events are emitted by the Manager since Socket.IO v3+
      this.socket.io.on('reconnect_attempt', (attempt: number) => {
        console.warn('[Socket] reconnect_attempt', { url, attempt });
      });

      this.socket.io.on('reconnect', (attempt: number) => {
        this.isConnected = true;
        console.log('[Socket] reconnected', { url, attempt, id: this.socket?.id });
      });

      this.socket.io.on('reconnect_error', (err: Error) => {
        console.error('[Socket] reconnect_error', { url, message: err.message, error: err });
      });

      this.socket.io.on('reconnect_failed', () => {
        console.error('[Socket] reconnect_failed', { url });
      });

      this.socket.on('disconnect', (reason) => {
        this.isConnected = false;
        console.warn('[Socket] disconnected', { url, reason });
      });
    } else {
      this.socket.connect();
    }
  }

  public disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
    }
  }

  public softDisconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.isConnected = false;
    }
  }

  public send(event: string, data: any): void {
    if (this.socket) {
      this.socket.emit(event, data);
    } else {
      console.error('Socket is not connected');
    }
  }

  public on(event: string, callback: (data: any) => void): void {
    if (this.socket) {
      this.socket?.on(event, callback);
    }
  }

  public off(event: string, callback?: (data: any) => void): void {
    this.socket?.off(event, callback);
  }

  public emit(event: string, ...args: any[]): void {
    this.socket?.emit(event, ...args);
  }

  public getSocketId(): string | null {
    return this.socket?.id || null;
  }

  public request(event: string, data: any = {}): Promise<any> {
    return new Promise((resolve, reject) => {
      if (this.socket) {
        // Use Socket.IO's built-in acknowledgment mechanism
        this.socket.emit(event, data, (response: any) => {
          if (response === null || response === undefined) {
            reject(new Error('No response received from server'));
          } else if (response.error) {
            reject(response.error);
          } else {
            resolve(response);
          }
        });
      } else {
        reject('Socket is not connected');
      }
    });
  }

  public getSocketConnectionState() {
    return this.isConnected;
  }
}

export default SocketService.getInstance();
